import { useEffect, useState } from "react";
import localforage from "localforage";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { negotiateAPI } from "../..";
import { isMockNegotiation } from "../../services/negotiationBridge";
import { newIdempotencyKey } from "../../services/pixAutomatico";
import { getErrorStatus } from "../../utils/userFacingError";

export type BidSession = { org_id: string; iss: string };
export const BID_ATTEMPT_RESOLVED_EVENT = "dakasa:bid-attempt-resolved";
export type BidAttempt = {
  key: string;
  orgId: string;
  collaboratorId: string;
  payload: { postId: string; lastBidAmount: number; lastBidDiff: number };
};
export type BidActionContext = {
  session: BidSession;
  postId: string;
  storageKey: string;
  post: AxiosInstance["post"];
};

/** Capture synchronously at the click, before reading JWT or IndexedDB. */
export function captureBidContext(session: BidSession, postId: string): BidActionContext {
  return {
    session: { org_id: session.org_id, iss: session.iss },
    postId,
    storageKey: storageKey(session, postId),
    post: negotiateAPI.post,
  };
}

type BidFlight = {
  promise: Promise<void>;
  attempt?: BidAttempt;
  observers: Set<(attempt: BidAttempt) => void>;
};
const inFlight = new Map<string, BidFlight>();
const listeners = new Set<(storageKey: string, attempt: BidAttempt | undefined) => void>();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validSession(session: BidSession) {
  return (
    typeof session.org_id === "string" &&
    session.org_id.length > 0 &&
    typeof session.iss === "string" &&
    session.iss.length > 0
  );
}

function storageKey(session: BidSession, postId: string) {
  const namespace = isMockNegotiation() ? "mock" : "live";
  return `negotiatePendingBid.v1:${namespace}:${encodeURIComponent(session.org_id)}:${encodeURIComponent(session.iss)}:${encodeURIComponent(postId)}`;
}

async function pendingBidAtKey(key: string, session: BidSession, postId: string) {
  const raw = await localforage.getItem<BidAttempt>(key);
  if (raw == null) return undefined;
  if (
    !UUID.test(raw.key) ||
    raw.orgId !== session.org_id ||
    raw.collaboratorId !== session.iss ||
    raw.payload?.postId !== postId ||
    !Number.isSafeInteger(raw.payload.lastBidAmount) ||
    raw.payload.lastBidAmount <= 0 ||
    !Number.isSafeInteger(raw.payload.lastBidDiff) ||
    raw.payload.lastBidDiff <= 0
  )
    throw new Error("The pending bid cannot be read safely.");
  return raw;
}

export function pendingBidAttempt(session: BidSession, postId: string) {
  return pendingBidAtKey(storageKey(session, postId), session, postId);
}

function notify(key: string, attempt?: BidAttempt) {
  listeners.forEach((listener) => listener(key, attempt));
}

// Only documented auction decisions prove this frozen attempt cannot stand.
// Authentication/rate-limit middleware may refuse a replay before reading its
// ledger, so those failures never discard an earlier unresolved bid.
const FINAL_REFUSALS = new Set([
  "bid_not_placed",
  "bid_moved",
  "bid_tax_moved",
  "already_last_bidder",
  "same_org",
  "bid_cooldown",
  "cooldown",
  "wait",
  "negotiation_closed",
  "negotiation_not_ready",
  "cycle_sealed",
  "cycle_crosses_week",
  "bid_not_allowed",
  "weekly_bids_disabled",
  "bid_amount_limit",
  // Ads refuses an increment below the company's minimum (R$10, or R$3 for
  // DaKasa's own company). The frozen body keeps that increment, so a replay
  // can never land: the next bid is a new attempt with another chip.
  "bid_increment_below_minimum",
  "tax_bound_exceeded",
  // Payments internal/hallweekly/types.go: every one is a recorded refusal
  // projected by Ads as its own BidRefusalResponse.
  "ceiling",
  "ceiling_below_pix_fee",
  "debt",
  "inactive",
  "period",
  "capacity",
  "cycle_settled",
  "cross_week",
  "not_weekly",
  "disabled",
  "quote_expired",
  "quote_fee_policy",
  // Saldo de Negociações (prepaid Hall rail): refused by the Ads precheck or
  // by the Payments reserve. The balance is re-read from Payments afterwards;
  // the replayed body never carries an amount.
  "negotiation_balance_insufficient",
  "negotiation_balance_restricted",
  "negotiation_balance_disabled",
]);

/** Refusals that mean the Saldo de Negociações, not the bid, is the obstacle. */
export const NEGOTIATION_BALANCE_REFUSALS: ReadonlySet<string> = new Set([
  "negotiation_balance_insufficient",
  "negotiation_balance_restricted",
  "negotiation_balance_disabled",
]);

/** The refusal reason of a 409 bid answer, when it names one of the balance refusals. */
export function negotiationBalanceRefusal(error: unknown): string | undefined {
  const reason = (error as { response?: { data?: { reason?: unknown } } })?.response?.data?.reason;
  return typeof reason === "string" && NEGOTIATION_BALANCE_REFUSALS.has(reason)
    ? reason
    : undefined;
}

export function isRecordedBidRefusal(error: unknown) {
  const response = (
    error as { response?: { data?: { reason?: unknown; recordedOutcome?: unknown } } }
  )?.response;
  return (
    (getErrorStatus(error) === 403 ||
      getErrorStatus(error) === 409 ||
      getErrorStatus(error) === 429) &&
    typeof response?.data?.reason === "string" &&
    FINAL_REFUSALS.has(response.data.reason) &&
    response.data.recordedOutcome === true
  );
}

export class PendingBidError extends Error {
  constructor(
    public readonly attempt: BidAttempt,
    public readonly original: unknown,
  ) {
    super(
      "O resultado do lance enviado ainda não foi confirmado. Use Consultar lance enviado para verificar a mesma tentativa.",
    );
  }
}

export class BidCreationUnavailableError extends Error {
  constructor() {
    super(
      "Para enviar lances com segurança, use um navegador atualizado em uma conexão segura. As tentativas já enviadas continuam disponíveis para consulta.",
    );
  }
}

export class StaleBidAttemptError extends Error {
  constructor() {
    super(
      "Essa tentativa ou sessão mudou. Atualize as publicações para consultar o estado atual do lance.",
    );
  }
}

function tokenMatchesSession(token: unknown, session: BidSession) {
  if (typeof token !== "string") return false;
  try {
    const encoded = token.split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/");
    if (!encoded || encoded.length > 16_384) return false;
    const claims = JSON.parse(window.atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=")));
    return claims?.org_id === session.org_id && claims?.iss === session.iss;
  } catch {
    return false;
  }
}

export function supportsBidCreation() {
  return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";
}

/** An explicit consultation replays the frozen key/body; it never creates a second intent. */
export function submitBidAttempt(
  session: BidSession,
  payload: BidAttempt["payload"],
  config: AxiosRequestConfig,
  onSending: (attempt: BidAttempt) => void,
  options?: { context?: BidActionContext; expectedAttempt?: BidAttempt },
): Promise<void> {
  if (!validSession(session)) return Promise.reject(new Error("Session unavailable"));
  if (
    typeof payload.postId !== "string" ||
    !payload.postId ||
    !Number.isSafeInteger(payload.lastBidAmount) ||
    payload.lastBidAmount <= 0 ||
    !Number.isSafeInteger(payload.lastBidDiff) ||
    payload.lastBidDiff <= 0
  )
    return Promise.reject(new Error("Invalid bid candidate"));
  session = { org_id: session.org_id, iss: session.iss };
  payload = {
    postId: payload.postId,
    lastBidAmount: payload.lastBidAmount,
    lastBidDiff: payload.lastBidDiff,
  };
  config = {
    ...config,
    headers: { ...config.headers },
    params: config.params ? { ...config.params } : undefined,
  };
  const context = options?.context ?? captureBidContext(session, payload.postId);
  if (
    context.session.org_id !== session.org_id ||
    context.session.iss !== session.iss ||
    context.postId !== payload.postId ||
    !tokenMatchesSession(config.headers?.["X-JWT"] ?? config.headers?.["x-jwt"], context.session)
  )
    return Promise.reject(new StaleBidAttemptError());
  const key = context.storageKey;
  const expected = options?.expectedAttempt
    ? { ...options.expectedAttempt, payload: { ...options.expectedAttempt.payload } }
    : undefined;
  const running = inFlight.get(key);
  if (running) {
    if (
      expected &&
      (!running.attempt ||
        running.attempt.key !== expected.key ||
        running.attempt.orgId !== expected.orgId ||
        running.attempt.collaboratorId !== expected.collaboratorId ||
        running.attempt.payload.postId !== expected.payload.postId ||
        running.attempt.payload.lastBidAmount !== expected.payload.lastBidAmount ||
        running.attempt.payload.lastBidDiff !== expected.payload.lastBidDiff)
    )
      return Promise.reject(new StaleBidAttemptError());
    running.observers.add(onSending);
    if (running.attempt) onSending(running.attempt);
    return running.promise;
  }
  // The bridge can switch while IndexedDB persists the intent (closing a tour
  // changes mock mode). Bind its current transport before yielding, so a
  // tutorial bid can never cross into the production API after that await.
  const post = context.post;
  const flight = { observers: new Set([onSending]) } as BidFlight;
  const run = async () => {
    let attempt = await pendingBidAtKey(key, session, payload.postId);
    if (
      expected &&
      (!attempt ||
        attempt.key !== expected.key ||
        attempt.orgId !== expected.orgId ||
        attempt.collaboratorId !== expected.collaboratorId ||
        attempt.payload.postId !== expected.payload.postId ||
        attempt.payload.lastBidAmount !== expected.payload.lastBidAmount ||
        attempt.payload.lastBidDiff !== expected.payload.lastBidDiff)
    ) {
      notify(key, attempt);
      throw new StaleBidAttemptError();
    }
    if (!attempt) {
      if (!supportsBidCreation()) throw new BidCreationUnavailableError();
      if (config.signal?.aborted) throw new Error("Bid cancelled before submission");
      attempt = {
        key: newIdempotencyKey(),
        orgId: session.org_id,
        collaboratorId: session.iss,
        payload: { ...payload },
      };
      // Persist before POST. If storage is unavailable, no financial request
      // leaves with a key that a reload would lose.
      await localforage.setItem(key, attempt);
      notify(key, attempt);
    }
    attempt = Object.freeze({ ...attempt, payload: Object.freeze({ ...attempt.payload }) });
    flight.attempt = attempt;
    flight.observers.forEach((observer) => observer(attempt!));
    try {
      if (config.signal?.aborted) throw new Error("Bid cancelled after freezing");
      const request = post("/bid", attempt.payload, {
        ...config,
        headers: { ...config.headers, "Idempotency-Key": attempt.key },
      });
      const signal = config.signal as AbortSignal | undefined;
      let abort: (() => void) | undefined;
      try {
        const response = await new Promise<AxiosResponse>((resolve, reject) => {
          abort = () => reject(new Error("Bid response abandoned"));
          signal?.addEventListener("abort", abort, { once: true });
          if (signal?.aborted) abort();
          request.then(resolve, reject);
        });
        if (response.status !== 200) throw new Error("Bid answer has no final confirmation");
      } finally {
        if (abort) signal?.removeEventListener("abort", abort);
      }
      if (config.signal?.aborted) throw new Error("Bid answer abandoned");
      await localforage.removeItem(key);
      notify(key);
    } catch (error) {
      if (isRecordedBidRefusal(error)) {
        await localforage.removeItem(key);
        notify(key);
        throw error;
      }
      throw new PendingBidError(attempt, error);
    }
  };
  // Web Locks serialize the read/create/send boundary across same-origin tabs.
  // Without it, only a frozen attempt can be consulted: get/set cannot make
  // creating a new UUID atomic across tabs.
  const operation: Promise<void> = (async () => {
    if (supportsBidCreation()) await navigator.locks.request(`dakasa:${key}`, run);
    else await run();
  })();
  flight.promise = operation;
  inFlight.set(key, flight);
  void operation
    .finally(() => {
      if (inFlight.get(key) === flight) inFlight.delete(key);
    })
    .catch(() => {});
  return operation;
}

export function usePendingBidAttempts(
  session: BidSession | undefined,
  postIds: string[],
  includeRetired = false,
  scanVersion = 0,
) {
  const [attempts, setAttempts] = useState<Record<string, BidAttempt>>({});
  const [error, setError] = useState(false);
  const identity = JSON.stringify([
    session?.org_id,
    session?.iss,
    postIds,
    includeRetired,
    scanVersion,
    isMockNegotiation(),
  ]);
  useEffect(() => {
    let disposed = false;
    setAttempts({});
    setError(false);
    if (!session) return;
    if (!validSession(session)) {
      setError(true);
      return;
    }
    const keys = new Map(postIds.map((id) => [storageKey(session, id), id]));
    const prefix = storageKey(session, "");
    const revisions = new Map<string, number>();
    const update = (key: string, attempt?: BidAttempt) => {
      const id =
        keys.get(key) ??
        (includeRetired && key.startsWith(prefix)
          ? decodeURIComponent(key.slice(prefix.length))
          : undefined);
      if (disposed || !id) return;
      setAttempts((previous) => {
        const next = { ...previous };
        if (attempt) next[id] = attempt;
        else delete next[id];
        return next;
      });
    };
    const onChanged = (key: string, attempt?: BidAttempt) => {
      revisions.set(key, (revisions.get(key) ?? 0) + 1);
      update(key, attempt);
    };
    listeners.add(onChanged);
    void (async () => {
      if (includeRetired) {
        for (const key of await localforage.keys()) {
          if (key.startsWith(prefix)) keys.set(key, decodeURIComponent(key.slice(prefix.length)));
        }
      }
      await Promise.all(
        [...keys].map(async ([key, id]) => {
          const revision = revisions.get(key) ?? 0;
          const attempt = await pendingBidAtKey(key, session, id);
          if ((revisions.get(key) ?? 0) === revision) update(key, attempt);
        }),
      );
    })().catch(() => {
      if (!disposed) setError(true);
    });
    return () => {
      disposed = true;
      listeners.delete(onChanged);
    };
  }, [identity]); // eslint-disable-line react-hooks/exhaustive-deps
  return { attempts, error };
}
